import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './ToolPage.css';

const UpiQrGenerator = () => {
  const [formData, setFormData] = useState({
    upiId: '',
    name: '',
    amount: '',
    note: ''
  });

  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const generateUPIString = () => {
    const { upiId, name, amount, note } = formData;
    if (!upiId) return '';

    let upiString = `upi://pay?pa=${encodeURIComponent(upiId)}`;
    if (name) upiString += `&pn=${encodeURIComponent(name)}`;
    if (amount) upiString += `&am=${encodeURIComponent(amount)}`;
    if (note) upiString += `&tn=${encodeURIComponent(note)}`;
    upiString += '&cu=INR';

    return upiString;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const copyUPIString = () => {
    const upiString = generateUPIString();
    navigator.clipboard.writeText(upiString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.download = `upi-qr-${formData.upiId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const payWithUPI = () => {
    const upiString = generateUPIString();
    if (!upiString) return;
    window.location.href = upiString;
  };

  const upiString = generateUPIString();

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="tool-title">UPI QR Generator</h1>
        <p className="tool-description">
          Generate UPI payment QR codes for seamless digital payments
        </p>
      </div>

      <div className="tool-content">
        <div className="upi-container">
          {/* Form Section */}
          <div className="upi-form-section">
            <h3 className="upi-section-title">Payment Details</h3>

            <div className="input-group">
              <label className="input-label">
                UPI ID <span className="required">*</span>
              </label>
              <input
                type="text"
                name="upiId"
                value={formData.upiId}
                onChange={handleInputChange}
                placeholder="example@paytm"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label className="input-label">Recipient Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Business Name"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label className="input-label">Amount (Optional)</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">₹</span>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-field amount-input"
                />
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Payment Note</label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                placeholder="Payment for services..."
                rows="3"
                className="textarea-field"
              />
            </div>

            {formData.upiId && (
              <div className="upi-string-section">
                <label className="input-label">UPI String</label>
                <div className="upi-string-row">
                  <div className="upi-string-display">
                    {upiString}
                  </div>
                  <button onClick={copyUPIString} className="copy-btn">
                    {copied ? '✓' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* QR Code Section */}
          <div className="upi-qr-section">
            <h3 className="upi-section-title">QR Code</h3>

            {formData.upiId ? (
              <div className="qr-display">
                <div className="qr-code-wrapper" ref={qrRef}>
                  <QRCodeSVG
                    value={upiString}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>

                {formData.name && (
                  <div className="qr-info">
                    <p className="qr-name">{formData.name}</p>
                    <p className="qr-upi-id">{formData.upiId}</p>
                  </div>
                )}

                {formData.amount && (
                  <div className="qr-amount">
                    ₹ {parseFloat(formData.amount).toFixed(2)}
                  </div>
                )}

                <div className="qr-actions">
                  <button onClick={downloadQR} className="action-btn primary">
                    ↓ Download QR
                  </button>
                  <button onClick={payWithUPI} className="action-btn">
                    Pay with UPI App
                  </button>
                </div>
              </div>
            ) : (
              <div className="qr-placeholder">
                <div className="qr-placeholder-icon">⬜</div>
                <p className="qr-placeholder-text">No QR Code Generated</p>
                <p className="qr-placeholder-hint">
                  Enter your UPI ID to generate a payment QR code
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="upi-info">
          <h4 className="upi-info-title">ℹ How to use:</h4>
          <ul className="upi-info-list">
            <li>Enter your UPI ID (required) and optional payment details</li>
            <li>QR code generates automatically and can be downloaded</li>
            <li>Share the QR code with customers for instant UPI payments</li>
            <li>Works with all UPI apps: Google Pay, PhonePe, Paytm, and more</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UpiQrGenerator;
